import React, { ReactElement, useEffect, useState } from "react";
import { animated, useSpring } from "react-spring";
import TablePagination from "@material-ui/core/TablePagination";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import CustomFooterGraphic from "../../Requests/CustomFooterGraphic";
import CustomProgress from "../../../components/Alert/CustomProgress";
import ProductApi from "../../../api/services/ProductApi";
import { TesaPrototype, TesaPrototypePagedRes } from "../../../api/models/product/TesaPrototype";
import { getRowsPerPage, setRowsPerPage } from "../../../helper/UserSettingsHelper";
import useEnv from "../../../hooks/env";
import { fadeIn } from "../../../styles/springs";

interface PrototypeOverviewTableProps {
  tesaPrototypePagedRes: TesaPrototypePagedRes | undefined;
  setTesaPrototypePagedRes: any;
  handleEdit: (rowData: CustomTableRowData) => void;
}

const PrototypeOverviewTable = (props: PrototypeOverviewTableProps): ReactElement => {
  const { tesaPrototypePagedRes, setTesaPrototypePagedRes, handleEdit } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(getRowsPerPage());
  const springProps = useSpring(fadeIn);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPageIndex(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const rowsPerPage = parseInt(event.target.value, 10);
    setPageSize(rowsPerPage);
    setRowsPerPage(rowsPerPage);
  };

  const generateRows = (prototypeArr: TesaPrototype[] | undefined): void => {
    const prototypeRows =
      prototypeArr?.map((prototype) => {
        return {
          id: prototype.id,
          prototypeName: prototype.name,
          responsibleLaboratory: prototype.responsibleLaboratory?.name,
          responsibleDeveloper: prototype.responsibleDeveloper,
          description: prototype.description,
          creationDate: prototype.creationDate,
        };
      }) || [];

    setRows(prototypeRows as never);
  };

  useEffect(() => {
    generateRows(tesaPrototypePagedRes?.data);
    setIsLoading(false);
  }, [tesaPrototypePagedRes]);

  useEffect(() => {
    setIsLoading(true);
    productApi
      .getPrototypes("", pageIndex, pageSize)
      .then((res) => {
        setTesaPrototypePagedRes(res.data);
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        console.log(e);
      });
  }, [pageSize, pageIndex]);

  return isLoading ? (
    <CustomProgress />
  ) : (
    <>
      <animated.div style={springProps}>
        <CustomTable
          boldIndex={1}
          columns={[
            "ID",
            "Prototype Name",
            "Responsible Laboratory",
            "Responsible Developer",
            "Description",
            "Creation Date",
          ]}
          columnMaxWidths={["4rem", "15vw", "10rem", "9rem", "15vw", "6rem"]}
          actionColumnIndexes={[1]}
          formatDates
          rows={rows}
          handleEdit={handleEdit}
        />
        <TablePagination
          component="div"
          count={tesaPrototypePagedRes?.totalData || 0}
          page={pageIndex}
          onChangePage={handleChangePage}
          rowsPerPage={pageSize}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
        <CustomFooterGraphic />
      </animated.div>
    </>
  );
};

export default PrototypeOverviewTable;
